import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UsersDrawer from '../UsersDrawer';

import {
  FormControl,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FlexBox } from '../../../global/FlexBox';
import { Tree } from '../Tree';

import { Organization, UserWritePayload } from '@app/shared/types';
import { useSnackbar } from 'notistack';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { useUserManagementContext } from '../../context/UserManagementContext';
import { useIdentity } from '@internal/shared';
import { useUser, useUpdateUser, useInternalDomainCheck } from '../../hooks';
import { Confirm } from '../Confirm';
import { createPhoneChangeHandler } from '../../../../shared/utils';
import { useApi } from '@backstage/core-plugin-api';
import { logtoOIDCAuthApiRef } from '../../../../apis';
import {
  ACTIVE,
  DEACTIVATED,
  INTERNAL,
  EXTERNAL,
  INPUT_MAX_LENGTH,
  USER_MANAGEMENT_WRITE,
  USER_MANAGEMENT_ORG_ADMIN_ACCESS,
} from '../../constants';
import { userValidationSchema, UserFormData } from '../../validationSchemas';
import { PHONE_MAX_LENGTH } from '../../../../shared/constants';
import {
  getApiErrorMessage,
  getApiFieldErrorMessage,
} from '../../../../shared/api/getApiErrorMessage';
import {
  getUserOrganizationIds,
  resolveUserOrganization,
  toUserWritePayload,
} from '../../utils';

const DEFAULT_FORM_DATA: UserFormData = {
  email: '',
  username: '',
  organizationIds: [],
  isActive: true,
  isInternal: false,
  attributes: [],
  otherInfo: '',
  phoneNumber: '',
  companyName: '',
};

type ConfirmValues = {
  title: string;
  content: string;
  confirm?: string;
  cancel: string;
};

export const EditUserDrawer = () => {
  const {
    currentUser,
    currentUserData,
    templates,
    organizations,
    setIsUserEditDrawerOpen,
    isUserEditDrawerOpen,
    setCurrentUser,
    setCurrentUserData,
  } = useUserManagementContext();
  const { minimizeDrawer } = useMinimizedDrawers();
  const { enqueueSnackbar } = useSnackbar();

  const { data: fetchedUserData, isLoading } = useUser(
    currentUserData ? undefined : currentUser,
  );
  const userData = currentUserData || fetchedUserData;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmValues, setConfirmValues] = useState<ConfirmValues>({
    title: '',
    content: '',
    cancel: '',
  });
  const [attributesChanged, setAttributesChanged] = useState(false);

  const {
    mutate: updateUser,
    isPending,
    reset: resetMutation,
  } = useUpdateUser();
  const { profile } = useIdentity();
  const authApi = useApi(logtoOIDCAuthApiRef);

  const { loggedInUserData } = useUserManagementContext();

  const userHasWritePermission = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_WRITE,
  );
  const userHasOrgAdminAccess = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_ORG_ADMIN_ACCESS,
  );

  const hasWritePermissions = userHasWritePermission || userHasOrgAdminAccess;

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm<UserFormData>({
    resolver: yupResolver(userValidationSchema),
    defaultValues: DEFAULT_FORM_DATA,
    mode: 'onSubmit',
  });

  const watchedForm = watch();
  const isInternalDomain = useInternalDomainCheck(watchedForm.email ?? '');

  const resetForm = useCallback(() => {
    reset();
    resetMutation();
    setAttributesChanged(false);
  }, [reset, resetMutation]);

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    const isActive = value === ACTIVE;
    setValue('isActive', isActive);
    setValue('attributes', isActive ? userData?.attributes || [] : []);
  };

  const handleDrawerClose = () => {
    setIsUserEditDrawerOpen(false);
    setCurrentUser(null);
    setCurrentUserData(null);
    resetForm();
  };

  const isOwner = userData?.email === profile?.email;

  const handleHide = () => {
    minimizeDrawer(
      userData?.id || '',
      'Edit User',
      toUserWritePayload(getValues(), userData?.id),
    );
    handleDrawerClose();
  };

  const handleUpdateUser = (formToSubmit: UserWritePayload, id: string) => {
    updateUser(
      {
        userId: id,
        userData: formToSubmit,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('User updated successfully', {
            variant: 'success',
          });
          if (!formToSubmit.isActive && isOwner) {
            authApi?.signOut();
            window.location.href = '/';
          }
          handleDrawerClose();
        },
        onError: error => {
          const emailError = getApiFieldErrorMessage(error, 'email');
          const phoneNumberError = getApiFieldErrorMessage(
            error,
            'phoneNumber',
          );

          if (emailError) {
            setError('email', {
              type: 'manual',
              message: emailError,
            });
          }
          if (phoneNumberError) {
            setError('phoneNumber', {
              type: 'manual',
              message: phoneNumberError,
            });
          }

          enqueueSnackbar(getApiErrorMessage(error, 'Failed to update user'), {
            variant: 'error',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!isUserEditDrawerOpen) {
      resetForm();
      setCurrentUser(null);
      setCurrentUserData(null);
    }
  }, [isUserEditDrawerOpen, resetForm, setCurrentUser, setCurrentUserData]);

  useEffect(() => {
    if (userData && isUserEditDrawerOpen) {
      setValue('email', userData.email);
      setValue('username', userData.username);
      setValue('phoneNumber', userData?.phoneNumber || '');
      setValue('otherInfo', userData.otherInfo || '');
      setValue('organizationIds', getUserOrganizationIds(userData));
      setValue('attributes', userData.attributes || []);
      setValue('isActive', userData.isActive);
      setValue('isInternal', userData.isInternal ?? false);
      setValue('companyName', userData.companyName || '');
      setAttributesChanged(false);
    }
  }, [userData, isUserEditDrawerOpen, setValue]);

  const onSubmit = async (data: UserFormData) => {
    if (!hasWritePermissions) {
      setIsConfirmOpen(true);
      setConfirmValues({
        title: 'User cannot be saved',
        content: 'You do not have enough permissions to edit user.',
        cancel: 'Cancel',
      });
      return;
    }

    if (!userData) return;

    clearErrors();

    const formToSubmit = toUserWritePayload(data, userData.id);

    const isRemovedSomeAttributes = userData.attributes.filter(
      (attr: string) => !formToSubmit.attributes.includes(attr),
    );
    const isRemovedEssentialAttributes =
      !formToSubmit.attributes.includes('app:dx-portal:full-access') &&
      !formToSubmit.attributes.includes(
        'app:dxm-user-management-panel:permissions:write',
      ) &&
      profile?.userId === userData.id;

    if (isRemovedEssentialAttributes) {
      setIsConfirmOpen(true);
      setConfirmValues({
        title: 'Are you sure you want to remove this role?',
        content:
          'The user will no longer be able to perform the permissions of this role for this account. ',
        confirm: 'Remove Role',
        cancel: 'Cancel',
      });
      return;
    }

    if (isRemovedSomeAttributes.length > 0) {
      setIsConfirmOpen(true);
      setConfirmValues({
        title: 'You are going to delete some of User permissions.',
        content: 'Are you sure you want to proceed?',
        confirm: 'Yes',
        cancel: 'No',
      });
      return;
    }

    handleUpdateUser(formToSubmit, userData.id!);
  };

  const handleSaveChanges = () => {
    handleSubmit(onSubmit)();
  };

  const handleConfirm = () => {
    if (!userData) return;
    if (!hasWritePermissions) return;

    handleUpdateUser(
      toUserWritePayload(getValues(), userData.id),
      userData.id!,
    );
  };

  useEffect(() => {
    if (
      userData &&
      attributesChanged &&
      watchedForm.attributes?.length === 0 &&
      userData.attributes?.length > 0
    ) {
      setValue('isActive', false);
    }
  }, [watchedForm.attributes, setValue, userData, attributesChanged]);

  return (
    <UsersDrawer
      id="edit-user-drawer"
      data-testid="edit-user-drawer"
      title="Edit User"
      hasHide
      handleDrawerClose={handleDrawerClose}
      isOpen={isUserEditDrawerOpen}
      handleHide={handleHide}
    >
      {isLoading ? (
        <FlexBox
          sx={{
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </FlexBox>
      ) : (
        <>
          <FormControl
            style={{
              display: 'flex',
              width: '100%',
              gap: 32,
              flexDirection: 'column',
              marginBottom: 24,
            }}
          >
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="Email Address"
                  required
                  type="email"
                  label="Email Address"
                  data-testid="edit-user-email-input"
                  InputLabelProps={{ shrink: true }}
                  disabled={isPending}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="username"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="User Name"
                  label="User Name"
                  required
                  data-testid="edit-user-username-input"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    maxLength: INPUT_MAX_LENGTH,
                    'data-testid': 'edit-user-username-native-input',
                  }}
                  disabled={isPending}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="Phone Number"
                  label="Phone Number"
                  type="tel"
                  data-testid="edit-user-phone-input"
                  InputLabelProps={{ shrink: true }}
                  disabled={isPending}
                  inputProps={{
                    maxLength: PHONE_MAX_LENGTH,
                    'data-testid': 'edit-user-phone-native-input',
                  }}
                  onChange={createPhoneChangeHandler(field.onChange)}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name="companyName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="Company Name"
                  label="Company Name"
                  data-testid="edit-user-company-input"
                  InputLabelProps={{ shrink: true }}
                  disabled={isPending}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="otherInfo"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  placeholder="Other Info"
                  label="Other Info"
                  data-testid="edit-user-other-info-input"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="organizationIds"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const selectedOrgs = (field.value || []).map(orgId =>
                  resolveUserOrganization({
                    orgId,
                    organizations: organizations?.data,
                    userOrganizations: userData?.organizations,
                  }),
                );

                return (
                  <Autocomplete
                    multiple
                    id="edit-organizations"
                    options={organizations?.data || []}
                    getOptionLabel={(option: Organization) => option.name || ''}
                    filterSelectedOptions
                    value={selectedOrgs}
                    onChange={(_, value) => {
                      const orgIds = value.map(org => org.id || '');
                      field.onChange(orgIds);
                    }}
                    disabled={isPending}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Team"
                        placeholder={!field.value?.length ? 'Team' : ''}
                        required
                        data-testid="edit-user-team-input"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                );
              }}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  id="select"
                  label="Status"
                  data-testid="edit-user-status-input"
                  value={field.value ? ACTIVE : DEACTIVATED}
                  onChange={e => {
                    handleStatusChange(e);
                  }}
                  select
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={isPending}
                >
                  <MenuItem value={ACTIVE}>Active</MenuItem>
                  <MenuItem value={DEACTIVATED}>Deactivated</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="isInternal"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  variant="outlined"
                  label="Access Category"
                  data-testid="edit-user-type-input"
                  value={field.value ? INTERNAL : EXTERNAL}
                  onChange={e => {
                    field.onChange(e.target.value === INTERNAL);
                  }}
                  select
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={isPending || isInternalDomain}
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value={INTERNAL}>Internal</MenuItem>
                  <MenuItem value={EXTERNAL}>External</MenuItem>
                </TextField>
              )}
            />

            {!!templates.length && (
              <Controller
                name="attributes"
                control={control}
                render={({ field }) => (
                  <Tree
                    handleChange={newAttributes => {
                      setAttributesChanged(true);
                      field.onChange(newAttributes);
                    }}
                    templates={templates}
                    attributes={userData?.attributes}
                    value={field.value || []}
                  />
                )}
              />
            )}
          </FormControl>

          <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveChanges}
              disabled={isPending}
              startIcon={
                isPending ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                    style={{ marginRight: 8 }}
                  />
                ) : null
              }
            >
              Save Changes
            </Button>
          </FlexBox>
        </>
      )}
      <Confirm
        isOpen={isConfirmOpen}
        title={confirmValues.title}
        content={confirmValues.content}
        confirm={confirmValues.confirm}
        cancel={confirmValues.cancel}
        onConfirm={() => {
          setIsConfirmOpen(false);
          handleConfirm();
        }}
        onClose={() => setIsConfirmOpen(false)}
      />
    </UsersDrawer>
  );
};
